import { TellescopeModelFromName, useTellescopeInfiniteQuery } from "@tellescope/tanstack-query";
import { type VirtualizedColumnDef, type VirtualizedDataTable, useUncontrolledState } from "@tellescope/ui";
import { ComponentProps, Dispatch, SetStateAction, useCallback, useMemo } from "react";

export const TELLESCOPE_2_TABLE_VIEW_PREFIX = "T2-" as const;

export type TableViewPage = "Tickets" | "Endusers" | "Orders" | string;

export interface TableViewColumnConfig<TRecord> {
  field: string;
  partialColumn: Partial<VirtualizedColumnDef<TRecord>>;
}

export interface UseTableViewArgs {
  page: TableViewPage;
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
}

/** Reorder item payload - the new index for each item after drag-drop */
export interface ReorderItem {
  id: string;
  index: number;
}

export interface UseTableViewResult<TRecord>
  extends Required<Pick<ComponentProps<typeof VirtualizedDataTable>, "sorting" | "setSorting">> {
  views: TellescopeModelFromName<"table_views">[];
  selectedView: TellescopeModelFromName<"table_views">;
  columns: VirtualizedColumnDef<TRecord>[];
  viewId: string;
  setViewId: Dispatch<SetStateAction<string>>;
  isLoadingViews: boolean;
  /** Whether the selected view is the local default (not a DB-persisted view) */
  isDefaultView: boolean;
  /** Callback to handle reorder - wraps the user-provided onReorder if sorting allows drag-drop */
  handleReorder?: (reorderedItems: ReorderItem[]) => void;
}

export interface TableViewConfig<TRecord>
  extends Pick<ComponentProps<typeof VirtualizedDataTable>, "sorting" | "setSorting"> {
  page: `${typeof TELLESCOPE_2_TABLE_VIEW_PREFIX}${string}`;
  defaultView: TellescopeModelFromName<"table_views">;
  fieldToColumnConfigMap: Record<string, Partial<VirtualizedColumnDef<TRecord>>>;
  fallbackCell?: VirtualizedColumnDef<TRecord>["cell"];
  /**
   * Callback when items are reordered via drag-drop.
   * Only invoked when default sorting (by index) is active.
   */
  onReorder?: (reorderedItems: ReorderItem[]) => void;
  sortable?: boolean;
  /** Whether to include a hidden index column for drag-drop reordering. Defaults to false. */
  enableIndexColumn?: boolean;
  /**
   * Optional override for which column fields (and their order) to render. When
   * provided, supersedes `selectedView.columns` so locally-staged column changes
   * (e.g. from a user without `update` permission on `table_views`) drive the
   * rendered table.
   */
  columnFieldsOverride?: string[];
}

/**
 * Generic hook for managing table views with column configuration
 *
 * @example
 * ```typescript
 * const { views, selectedView, columns, viewId, setViewId } = useTableView({
 *   page: "Tickets",
 *   defaultView: DEFAULT_TICKET_VIEW,
 *   fieldToColumnConfigMap: TICKET_FIELD_MAP,
 *   viewId: "default-view",
 * });
 * ```
 */
export function useTableView<TRecord = unknown>({
  page,
  defaultView,
  fieldToColumnConfigMap,
  fallbackCell,
  viewId: providedViewId,
  setViewId: providedSetViewId,
  sorting: providedSorting,
  setSorting: providedSetSorting,
  onReorder,
  sortable = true,
  enableIndexColumn = false,
  columnFieldsOverride,
}: TableViewConfig<TRecord> & UseTableViewArgs): UseTableViewResult<TRecord> {
  const [viewId, setViewId] = useUncontrolledState({
    state: providedViewId ?? "default-view",
    setState: providedSetViewId,
  });
  const [sorting, setSorting] = useUncontrolledState({
    state: providedSorting?.length ? providedSorting : [],
    setState: providedSetSorting,
  });

  const viewsQuery = useTellescopeInfiniteQuery({
    model: "table_views",
    method: "getSome",
    request: {
      limit: 500,
      filter: {
        page,
      },
    },
    select: (data) => data.pages.flat(),
  });

  const isDefaultView = useMemo(() => {
    if (viewId === "default") return true;
    return !viewsQuery.data?.some((view) => view.id === viewId);
  }, [viewId, viewsQuery.data]);

  const selectedView = useMemo<TellescopeModelFromName<"table_views">>(() => {
    return isDefaultView ? defaultView : viewsQuery.data?.find((view) => view.id === viewId) ?? defaultView;
  }, [isDefaultView, viewId, viewsQuery.data, defaultView]);

  const columns = useMemo<VirtualizedColumnDef<TRecord>[]>(() => {
    const sourceFields = columnFieldsOverride ?? selectedView.columns.map((col) => col.field);
    const mappedColumns = sourceFields.reduce<VirtualizedColumnDef<TRecord>[]>((acc, field) => {
      if (field === "inded") return acc;
      const config = fieldToColumnConfigMap[field];
      if (config) {
        // Merge config with field as id (config properties like accessorKey take precedence)
        acc.push({
          ...config,
          id: field,
          sortable,
        });
        return acc;
      }

      // Fallback for unknown fields - no width specified
      acc.push({
        id: field,
        label: field,
        cell:
          fallbackCell ??
          (({ row }) => {
            const value =
              row.original && typeof row.original === "object" && field in row.original
                ? row.original[field as keyof typeof row.original]
                : undefined;
            return value ? String(value) : null;
          }),
        sortable,
      });
      return acc;
    }, []);

    // Separate Actions column from other columns
    const actionsIndex = mappedColumns.findIndex((col) => col.id === "Actions");
    const actionsColumn = actionsIndex !== -1 ? mappedColumns[actionsIndex] : null;
    const otherColumns = actionsIndex !== -1 ? mappedColumns.filter((_, idx) => idx !== actionsIndex) : mappedColumns;

    // Build final column list: other columns, Actions column (if present), then optionally hidden index column
    return [
      ...otherColumns,
      ...(actionsColumn ? [actionsColumn] : []),
      ...(enableIndexColumn
        ? [
            {
              id: "index",
              label: "index",
              width: 100,
              accessorKey: "index",
              visible: false,
              sortable: true,
            },
          ]
        : []),
    ];
  }, [selectedView.columns, fieldToColumnConfigMap, fallbackCell, sortable, enableIndexColumn, columnFieldsOverride]);

  // Determine if drag-drop should be enabled based on sorting state
  // Only enable when sorting by default (index ascending)
  const isDragDropEnabled = useMemo(() => {
    if (sorting.length !== 1) return false;
    const sort = sorting[0];
    if (!sort) return false;
    return sort.id === "index" && sort.desc === false;
  }, [sorting]);

  // Wrap the onReorder callback - only invoke if drag-drop is enabled
  const handleReorder = useCallback(
    (reorderedItems: ReorderItem[]) => {
      if (isDragDropEnabled && onReorder) {
        onReorder(reorderedItems);
      }
    },
    [isDragDropEnabled, onReorder]
  );

  return {
    views: viewsQuery.data ?? [],
    selectedView,
    columns,
    viewId,
    setViewId,
    isLoadingViews: viewsQuery.isLoading,
    isDefaultView,
    sorting,
    setSorting,
    handleReorder: onReorder ? handleReorder : undefined,
  };
}

/**
 * Hook for managing column visibility based on table view configuration
 *
 * @example
 * ```typescript
 * const columnVisibility = useTableViewColumnVisibility({
 *   selectedView,
 *   viewsVisibleColumns: store.viewsVisibleColumns,
 * });
 * ```
 */
export function useTableViewColumnVisibility({
  selectedView,
  viewsVisibleColumns,
}: {
  selectedView: TellescopeModelFromName<"table_views">;
  viewsVisibleColumns: Record<string, Record<string, boolean>>;
}): Record<string, boolean> {
  return useMemo<Record<string, boolean>>(() => {
    const visibleColumns = viewsVisibleColumns[selectedView.id];
    return selectedView.columns.reduce<Record<string, boolean>>((acc, curr) => {
      const isVisible = visibleColumns?.[curr.field] ?? true;
      acc[curr.field] = isVisible;
      return acc;
    }, {});
  }, [selectedView.columns, selectedView.id, viewsVisibleColumns]);
}
